import type {
  AcademicRiskManagementRepository,
  CreateManagementPayload,
  CreateManagementResponse,
  DeleteManagementResponse,
  GetManagementResponse,
  UpdateManagementPayload,
  UpdateManagementResponse,
} from '@/domains/academicRisk';
import { academicRiskManagementApiService } from '../http/academicRiskManagement.api';

/**
 * Implementación del repositorio de gestión/ejecución de acciones académicas usando API service
 * Actúa como intermediario entre el dominio y la capa HTTP para operaciones de seguimiento
 * y ejecución de acciones, validando respuestas antes de devolverlas
 */
class AcademicRiskManagementApiRepository
  implements AcademicRiskManagementRepository
{
  /**
   * Obtiene todas las gestiones/ejecuciones asociadas a una incorporación de acción
   * @param cod_incorporacion_accion - Código único de la incorporación de acción
   * @throws Error si no se obtienen datos válidos del API
   */
  async getManagement(
    cod_incorporacion_accion: number,
  ): Promise<GetManagementResponse> {
    const getResponse = await academicRiskManagementApiService.getManagement(
      cod_incorporacion_accion,
    );

    if (!getResponse)
      throw new Error('Error al obtener gestiones sobre las acciones');

    return getResponse;
  }

  /**
   * Registra una nueva gestión/ejecución de una acción académica
   * @param payload - Datos de la gestión incluyendo fecha, funcionario y detalle
   * @throws Error si la operación de creación falla
   */
  async createManagement(
    payload: CreateManagementPayload,
  ): Promise<CreateManagementResponse> {
    const createResponse =
      await academicRiskManagementApiService.createManagement(payload);

    if (!createResponse)
      throw new Error('Error al crear la gestión sobre la acción');

    return createResponse;
  }

  /**
   * Actualiza una gestión/ejecución de acción existente
   * @param cod_ejecucion - Código único de la ejecución a actualizar
   * @param payload - Datos actualizados de la gestión
   * @throws Error si la operación de actualización falla
   */
  async updateManagement(
    cod_ejecucion: number,
    payload: UpdateManagementPayload,
  ): Promise<UpdateManagementResponse> {
    const updateResponse =
      await academicRiskManagementApiService.updateManagement(
        cod_ejecucion,
        payload,
      );

    if (!updateResponse)
      throw new Error('Error al actualizar la gestión sobre la acción');

    return updateResponse;
  }

  /**
   * Elimina una gestión/ejecución de acción del sistema
   * @param cod_ejecucion - Código único de la ejecución a eliminar
   * @throws Error si la operación de eliminación falla
   */
  async deleteManagement(
    cod_ejecucion: number,
  ): Promise<DeleteManagementResponse> {
    const deleteResponse =
      await academicRiskManagementApiService.deleteManagement(cod_ejecucion);

    if (!deleteResponse)
      throw new Error('Error al elimnar la gestión sobre la acción');

    return deleteResponse;
  }
}

export const academicRiskManagementApiRepository =
  new AcademicRiskManagementApiRepository();
